// Field renderers for custom stage form fields
// Each field type has its own renderer component

import { useState } from "react";
import {
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import { Check, Upload, X, Loader2, AlertCircle } from "lucide-react";
import type { FieldSchema } from "@/lib/queries/custom-stages";

// ============================================================================
// Constants
// ============================================================================

const ACCEPTED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/bmp",
  "image/svg+xml",
  "image/heic",
  "image/heif",
  "image/tiff",
  "video/mp4",
  "video/quicktime",
  "video/x-msvideo",
  "video/x-ms-wmv",
  "video/x-matroska",
  "video/webm",
  "video/x-m4v",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const ACCEPTED_FILE_EXTENSIONS =
  ".jpg,.jpeg,.png,.gif,.webp,.bmp,.svg,.heic,.heif,.tiff,.tif,.mp4,.mov,.avi,.wmv,.mkv,.webm,.m4v,.pdf,.docx";

const ACCEPTED_EXTENSIONS_SET = new Set(ACCEPTED_FILE_EXTENSIONS.split(","));
const ACCEPTED_MIME_TYPES_SET = new Set(ACCEPTED_FILE_TYPES);

function isAcceptedFile(file: File): boolean {
  if (ACCEPTED_MIME_TYPES_SET.has(file.type)) return true;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return ACCEPTED_EXTENSIONS_SET.has(`.${ext}`);
}

// ============================================================================
// Types
// ============================================================================

export interface FieldRendererProps {
  field: FieldSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean | null;
  onFileSelect?: (file: File, fieldId: string) => Promise<void>;
  uploadState?: UploadFieldState;
  onRemoveFile?: (fieldId: string) => void;
}

export interface UploadFieldState {
  file: File | null;
  status: "idle" | "getting-url" | "ready" | "uploading" | "success" | "error";
  progress: number;
  uploadedUrl: string | null;
  errorMessage: string | null;
  fileName: string | null;
}

// ============================================================================
// Helper Components
// ============================================================================

function FieldLabel({
  name,
  isMandatory,
}: {
  name: string;
  isMandatory: boolean;
}) {
  return (
    <Label className="text-sm font-medium text-foreground">
      {name} {isMandatory && <span className="text-destructive">*</span>}
    </Label>
  );
}

// ============================================================================
// Field Renderers
// ============================================================================

export function TextField({
  field,
  value,
  onChange,
  disabled,
}: FieldRendererProps) {
  return (
    <div className="space-y-2">
      <FieldLabel name={field.name} isMandatory={field.isMandatory} />
      <Input
        type="text"
        value={(value as string) || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter text"
        disabled={disabled ?? undefined}
      />
    </div>
  );
}

export function NumberField({
  field,
  value,
  onChange,
  disabled,
}: FieldRendererProps) {
  return (
    <div className="space-y-2">
      <FieldLabel name={field.name} isMandatory={field.isMandatory} />
      <Input
        type="number"
        value={(value as number) ?? ""}
        onChange={(e) => onChange(parseInt(e.target.value) || 0)}
        placeholder="Enter number"
        disabled={disabled ?? undefined}
      />
    </div>
  );
}

export function BooleanField({
  field,
  value,
  onChange,
  disabled,
}: FieldRendererProps) {
  const boolValue = value as boolean | undefined;

  const isDisabled = disabled ?? undefined;

  // If description exists, show checkbox with description
  if (field.description) {
    return (
      <div className="space-y-3">
        <FieldLabel name={field.name} isMandatory={field.isMandatory} />
        <div className="flex items-center gap-3">
          <Checkbox
            checked={boolValue || false}
            onCheckedChange={(checked) => onChange(checked === true)}
            disabled={isDisabled}
          />
          <span className="text-sm text-foreground">{field.description}</span>
        </div>
      </div>
    );
  }

  // If no description, show Yes/No buttons
  return (
    <div className="space-y-3">
      <FieldLabel name={field.name} isMandatory={field.isMandatory} />
      <div className="flex gap-3">
        <Button
          type="button"
          variant={boolValue === true ? "secondary" : "outline"}
          onClick={() => onChange(true)}
          disabled={isDisabled}
          size="sm"
        >
          Yes
        </Button>
        <Button
          type="button"
          variant={boolValue === false ? "secondary" : "outline"}
          onClick={() => onChange(false)}
          disabled={isDisabled}
          size="sm"
        >
          No
        </Button>
      </div>
    </div>
  );
}

export function DateField({
  field,
  value,
  onChange,
  disabled,
}: FieldRendererProps) {
  return (
    <div className="space-y-2">
      <FieldLabel name={field.name} isMandatory={field.isMandatory} />
      <Input
        type="date"
        value={(value as string) || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled ?? undefined}
      />
    </div>
  );
}

export function SingleSelectField({
  field,
  value,
  onChange,
  disabled,
}: FieldRendererProps) {
  const options = (field.metaData?.options as string[]) || [];

  return (
    <div className="space-y-2">
      <FieldLabel name={field.name} isMandatory={field.isMandatory} />
      <Select
        value={(value as string) || ""}
        onValueChange={onChange}
        disabled={disabled ?? undefined}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select an option" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function MultiSelectField({
  field,
  value,
  onChange,
  disabled,
}: FieldRendererProps) {
  const options = (field.metaData?.options as string[]) || [];
  const selectedValues = (value as string[]) || [];
  const isDisabled = disabled ?? undefined;

  const toggleOption = (option: string) => {
    if (selectedValues.includes(option)) {
      onChange(selectedValues.filter((v) => v !== option));
    } else {
      onChange([...selectedValues, option]);
    }
  };

  return (
    <div className="space-y-2">
      <FieldLabel name={field.name} isMandatory={field.isMandatory} />
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const isSelected = selectedValues.includes(option);
          return (
            <Button
              key={option}
              type="button"
              variant={isSelected ? "secondary" : "outline"}
              onClick={() => toggleOption(option)}
              disabled={isDisabled}
              size="sm"
              className="flex items-center gap-1"
            >
              {isSelected && <Check className="h-3 w-3" />}
              {option}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

export function CheckboxField({
  field,
  value,
  onChange,
  disabled,
}: FieldRendererProps) {
  return (
    <div className="flex items-start gap-2">
      <Checkbox
        id={`field_${field.id}`}
        checked={(value as boolean) || false}
        onCheckedChange={(checked) => onChange(checked === true)}
        disabled={disabled ?? undefined}
        className="mt-0.5"
      />
      <label
        htmlFor={`field_${field.id}`}
        className="text-sm text-foreground leading-relaxed cursor-pointer"
      >
        {field.name}{" "}
        {field.isMandatory && <span className="text-destructive">*</span>}
      </label>
    </div>
  );
}

export function UploadField({
  field,
  disabled,
  onFileSelect,
  uploadState,
  onRemoveFile,
}: FieldRendererProps) {
  const fieldId = `field_${field.id}`;
  const state = uploadState || {
    file: null,
    status: "idle" as const,
    progress: 0,
    uploadedUrl: null,
    errorMessage: null,
    fileName: null,
  };

  const [fileError, setFileError] = useState<string | null>(null);

  const selectFile = (file: File) => {
    if (!isAcceptedFile(file)) {
      setFileError(
        `"${file.name}" is not a supported file type. Accepted: images, videos, PDF, DOCX.`,
      );
      return;
    }
    setFileError(null);
    onFileSelect?.(file, fieldId);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (disabled) return;
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      selectFile(droppedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleFileInput = () => {
    if (disabled) return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ACCEPTED_FILE_EXTENSIONS;
    input.onchange = (e) => {
      const selectedFile = (e.target as HTMLInputElement).files?.[0];
      if (selectedFile) {
        selectFile(selectedFile);
      }
    };
    input.click();
  };

  // Success state
  if (state.status === "success" && state.fileName) {
    return (
      <div className="space-y-2">
        <FieldLabel name={field.name} isMandatory={field.isMandatory} />
        <div className="border border-green-500 rounded-lg bg-green-50 dark:bg-green-950 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium">{state.file?.name}</p>
                <p className="text-xs text-green-600 dark:text-green-400">
                  File uploaded successfully
                </p>
              </div>
            </div>
            {!disabled && onRemoveFile && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFileError(null);
                  onRemoveFile(fieldId);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Ready state (file selected, ready for upload on submit)
  if (state.status === "ready") {
    return (
      <div className="space-y-2">
        <FieldLabel name={field.name} isMandatory={field.isMandatory} />
        <div className="border border-blue-500 rounded-lg bg-blue-50 dark:bg-blue-950 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <Upload className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium">{state.file?.name}</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  Ready to upload on submit
                </p>
              </div>
            </div>
            {!disabled && onRemoveFile && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFileError(null);
                  onRemoveFile(fieldId);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (state.status === "error") {
    return (
      <div className="space-y-2">
        <FieldLabel name={field.name} isMandatory={field.isMandatory} />
        <div className="border border-destructive rounded-lg bg-destructive/10 p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-destructive rounded-lg flex items-center justify-center">
              <AlertCircle className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">Upload failed</p>
              <p className="text-xs text-destructive">{state.errorMessage}</p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => state.file && onFileSelect?.(state.file, fieldId)}
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (state.status === "getting-url" || state.status === "uploading") {
    return (
      <div className="space-y-2">
        <FieldLabel name={field.name} isMandatory={field.isMandatory} />
        <div className="border border-primary rounded-lg bg-primary/10 p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Loader2 className="w-4 h-4 text-white animate-spin" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{state.file?.name}</p>
              <p className="text-xs text-primary">
                {state.status === "getting-url"
                  ? "Preparing upload..."
                  : "Uploading..."}
              </p>
              {state.status === "uploading" && (
                <div className="mt-2">
                  <div className="w-full bg-primary/20 rounded-full h-1.5">
                    <div
                      className="bg-primary h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${state.progress}%` }}
                    />
                  </div>
                  <p className="text-xs text-primary mt-1">
                    {state.progress}% uploaded
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default state - file input
  const isDisabled = disabled ?? false;
  return (
    <div className="space-y-2">
      <FieldLabel name={field.name} isMandatory={field.isMandatory} />
      <div
        className={`w-full p-4 bg-muted rounded-xl border border-dashed border-border flex flex-col items-center gap-2 transition-colors ${
          isDisabled
            ? "opacity-50 cursor-not-allowed"
            : "cursor-pointer hover:bg-muted/80"
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={handleFileInput}
      >
        <Button type="button" variant="outline" size="sm" disabled={isDisabled}>
          <Upload className="w-4 h-4 mr-2" />
          {isDisabled ? "File upload disabled" : "Upload File"}
        </Button>
        <p className="text-sm text-muted-foreground">
          {isDisabled ? "Already submitted" : "Images, videos, PDF, or DOCX"}
        </p>
      </div>
      {fileError && (
        <p className="text-xs text-destructive" role="alert">
          {fileError}
        </p>
      )}
    </div>
  );
}

export function UrlField({
  field,
  value,
  onChange,
  disabled,
}: FieldRendererProps) {
  const [touched, setTouched] = useState(false);
  const urlValue = (value as string) || "";

  const isValidUrl = (url: string): boolean => {
    if (!url.trim()) return true; // Empty is valid (unless mandatory)
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const hasValue = urlValue.trim().length > 0;
  const isValid = isValidUrl(urlValue);

  return (
    <div className="space-y-2">
      <FieldLabel name={field.name} isMandatory={field.isMandatory} />
      <div className="relative">
        <Input
          type="url"
          value={urlValue}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder="Enter URL"
          disabled={disabled ?? undefined}
          className={
            touched && hasValue && !isValid ? "border-destructive" : ""
          }
        />
        {hasValue && isValid && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Check className="w-4 h-4 text-green-500" />
          </div>
        )}
      </div>
      {touched && hasValue && !isValid && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Please enter a valid URL (e.g., https://example.com)
        </p>
      )}
    </div>
  );
}

export function CtaField({ field, disabled }: FieldRendererProps) {
  const ctaText =
    (field.metaData?.ctaText as string) ||
    (field.metaData?.ctaLabel as string) ||
    "Call To Action";
  const ctaUrl = field.metaData?.ctaUrl as string | undefined;
  const isDisabled = disabled ?? false;

  const handleClick = () => {
    if (ctaUrl && !isDisabled) {
      window.open(ctaUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="space-y-2">
      <FieldLabel name={field.name} isMandatory={false} />
      <Button
        type="button"
        variant="outline"
        className="w-full"
        disabled={isDisabled}
        onClick={handleClick}
      >
        {ctaText}
      </Button>
    </div>
  );
}
